import React, { useState, useEffect } from 'react';
import {
    Backdrop,
    Card,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox, ListItemText, OutlinedInput
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CustomFilter from 'src/components/filter/CustomFilter';
import Iconify from 'src/components/iconify';
import ClearButton from 'src/components/shared/ClearButton';
import DynamicTable from 'src/components/shared/DynamicTable';
import FilterButton from 'src/components/shared/FilterButton';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import Services from 'src/services/Services';
import moment from 'moment';
import { toast } from 'react-toastify';

export default function DhlReportList({ parsedUser, tabs }) {
    const [reportList, setReportList] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState({});
    const isMobile = useMediaQuery('(max-width:768px)');
    const [openDetail, setOpenDetail] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [formData, setFormData] = useState({});

    const uniqueValues = (key) =>
        Array.from(new Set((originalData || []).map(x => x?.[key]).filter(Boolean)))
            .sort((a, b) => String(a).localeCompare(String(b), "tr-TR"));

    const applyFilters = (data, currentFilters) => {
        if (!currentFilters || Object.keys(currentFilters).length === 0) return data;

        // Güvenli tarih dönüştürücü
        const asJsDate = (val, type = "start") => {
            if (!val) return null;
            if (moment.isMoment(val)) {
                return type === "start" ? val.startOf("day").toDate() : val.endOf("day").toDate();
            }
            if (val instanceof Date) return val;
            return new Date(val);
        };

        return (data || []).filter((item) => {
            return Object.entries(currentFilters).every(([key, filterValue]) => {
                // Tarih alanları
                if (key === "operationDate" || key === "deliveredDate") {
                    const itemDate = item[key] ? new Date(item[key] * 1000) : null;
                    const start = asJsDate(filterValue?.start, "start");
                    const end = asJsDate(filterValue?.end, "end");
                    if (start && itemDate && itemDate < start) return false;
                    if (end && itemDate && itemDate > end) return false;
                    // itemDate yoksa ve tarih filtresi varsa ele
                    if ((start || end) && !itemDate) return false;
                    return true;
                }

                // Çoklu seçmeli alanlar
                if (Array.isArray(filterValue)) {
                    if (!filterValue.length) return true;
                    const raw = item[key];
                    const val = typeof raw === "string" ? raw : String(raw ?? "");
                    return filterValue.map(String).includes(val);
                }

                // Serbest metin alanları
                if (!filterValue || (typeof filterValue === "string" && filterValue.trim() === "")) return true;

                let itemValue = item[key];
                if (typeof itemValue === "number") itemValue = String(itemValue);

                const columnDef = TABLE_HEAD.find((col) => col.id === key);
                if (columnDef?.format) {
                    try {
                        itemValue = columnDef.format(itemValue, item);
                    } catch (_) {
                        // format hatası olursa ham değerle devam
                    }
                }

                return (
                    itemValue &&
                    String(itemValue).toLocaleUpperCase("tr-TR").includes(String(filterValue).toLocaleUpperCase("tr-TR"))
                );
            });
        });
    };

    const TABLE_HEAD = [
        { id: 'operationDate', label: "İşlem Tarihi", format: (value) => formatDate(value) },
        { id: 'waybill', label: "Konşimento Numarası (Waybill)" },
        { id: 'senderName', label: "Gönderen Adı" },
        { id: 'receiverName', label: "Alıcı Adı" },
        { id: 'processingStaff', label: "İşlem Yapan EP Çalışanı" },
        { id: 'deliveredStaff', label: "Teslim Eden EP Çalışanı", format: (value) => value ? value : "-" },
        { id: 'deliveredDate', label: "Teslim Tarihi", format: (value) => formatDate(value) },
        { id: 'price', label: "Tutar (TL)", format: (value) => value ? value : "-" },
        { id: 'poiTitle', label: "Lokasyon" },
        { id: 'description', label: "Açıklama" },
        { id: 'additionalNote', label: "Not", format: (value) => value ? value : "-" },
        {
            id: 'action',
            label: '',
            alignRight: false,
            format: (value, row) => (
                <Tooltip title={"Detay"}>
                    <Iconify
                        style={{
                            color: '#98A2B3',
                            width: 22,
                            height: 22,
                            cursor: 'pointer',
                        }}
                        icon="bx:detail"
                        onClick={() => handleOpenDetail(row)}
                    />
                </Tooltip>
            ),
        }
    ];

    const formatDate = (timestampOrMoment) => {
        if (moment.isMoment(timestampOrMoment)) {
            return timestampOrMoment.format("DD.MM.YYYY");
        }
        const date = new Date(timestampOrMoment * 1000);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        if (year === 1970) {
            return "-"
        }
        else {
            return `${day}.${month}.${year}`;
        }
    };

    useEffect(() => {
        getReports();
    }, []);

    const getReports = (preserveFilters = false) => {
        setLoading(true);
        Services.GetOperationReport(parsedUser.token, "dhl")
            .then((res) => {
                if (res.data.status) {
                    const incoming = res.data.result || [];
                    setOriginalData(incoming);
                    const nextList =
                        preserveFilters && filters && Object.keys(filters).length > 0
                            ? applyFilters(incoming, filters)
                            : incoming;

                    setReportList(nextList);
                } else {
                    toast.error("Rapor bulunamadı.");
                    setReportList([]);
                    setOriginalData([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const handleModal = () => {
        setFilterModal(true);
    };

    const handleCloseModal = () => {
        setFilterModal(false);
    };

    const getFilteredDHLReports = () => {
        const filtered = applyFilters(originalData, filters);
        setReportList(filtered);
        handleCloseModal();
    };


    const clearFilters = () => {
        setFilters({});
        setReportList(originalData);
    };

    // Detay modal işlemleri
    const handleOpenDetail = (row) => {
        setSelectedRow(row);
        setFormData({
            ...row,
            operationDate: moment(row.operationDate * 1000),
            deliveredDate: row.deliveredDate > 0
                ? moment(row.deliveredDate * 1000)
                : null
        });
        setOpenDetail(true);
    };

    const handleCloseDetail = () => {
        setOpenDetail(false);
        setSelectedRow(null);
    };

    const handleFormChange = (field, value) => {
        setFormData({
            ...formData,
            [field]: value
        });
    };

    const isDetailFormValid = () => {
        if (!formData) return false;
        const requiredFields = [
            'operationDate',
            'waybill',
            'senderName',
            'receiverName',
            'processingStaff',
            'description'
        ];
        for (let field of requiredFields) {
            if (!formData[field]) return false;
            if (typeof formData[field] === 'string' && formData[field].trim() === '') return false;
        }
        return true;
    };

    const handleSaveDetail = () => {
        const formattedData = {
            operationID: formData.operationID,
            waybill: formData.waybill,
            senderName: formData.senderName,
            receiverName: formData.receiverName,
            processingStaff: formData.processingStaff,
            deliveredStaff: formData.deliveredStaff,
            price: formData.price,
            description: formData.description,
            operationDate: formData.operationDate.format("YYYY-MM-DD"),
            deliveredDate: formData.deliveredDate.format("YYYY-MM-DD"),
            additionalNote: formData.additionalNote
        };

        setLoading(true);
        Services.UpdateOperationReport(parsedUser.token, [formattedData], "dhl")
            .then((res) => {
                if (res.data.status) {
                    getReports(true);
                    handleCloseDetail();
                    toast.success("Rapor başarıyla güncellendi.")
                } else {
                    console.log(res.data);
                    toast.error("Rapor güncellenirken hata oluştu.");
                    setLoading(false);
                }
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    return (
        <div>
            <Stack direction="row" justifyContent="space-between" mb={2}>
                {tabs}
                {/* <div>
                    <ExportToExcelButton onClick={() => exportToExcel()} />
                </div> */}
            </Stack>
            <Stack
                direction="row"
                justifyContent={isMobile ? "space-between" : "end"}
                mb={5}
                paddingX={2}
            >
                {isMobile ? (
                    <FilterViewButton variant="contained" onClick={handleModal} />
                ) : null}
            </Stack>

            <CustomFilter
                modalType="drawer"
                filterModal={filterModal}
                handleCloseModal={handleCloseModal}
            >
                <Stack
                    direction="column"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    paddingY={2}
                    paddingX={1}
                >
                    {TABLE_HEAD.map((column) => {
                        if (column.id === "operationDate" || column.id === "deliveredDate") {
                            return (
                                <Stack key={column.id} direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label={`${column.label} Başlangıç`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.start || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), start: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                        <DatePicker
                                            label={`${column.label} Bitiş`}
                                            format="DD/MM/YYYY"
                                            value={filters[column.id]?.end || null}
                                            onChange={(newValue) => {
                                                setFilters({
                                                    ...filters,
                                                    [column.id]: { ...(filters[column.id] || {}), end: newValue }
                                                });
                                            }}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                </Stack>
                            );
                        } else if (column.id === "description") {
                            return (
                                <FormControl key={column.id} fullWidth margin="normal" variant="outlined">
                                    <InputLabel id="description-label">{column.label}</InputLabel>
                                    <Select
                                        labelId="description-label"
                                        id="description"
                                        value={filters[column.id] || ''}
                                        label={column.label}
                                        onChange={(e) =>
                                            setFilters({ ...filters, [column.id]: e.target.value })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Seçiniz</em>
                                        </MenuItem>
                                        {['Hazır Gönderi', 'Ücretli Gönderi', 'Amazon İadesi', 'DHL Dağıtım'].map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        }
                        else if (["poiTitle", "processingStaff", "deliveredStaff", "description"].includes(column.id)) {
                            const options = uniqueValues(column.id);
                            return (
                                <FormControl key={column.id} fullWidth margin="normal" variant="outlined">
                                    <InputLabel id={`${column.id}-label`}>{column.label}</InputLabel>
                                    <Select
                                        labelId={`${column.id}-label`}
                                        multiple
                                        input={<OutlinedInput label={column.label} />}
                                        renderValue={(selected) => (selected || []).join(", ")}
                                        value={filters[column.id] || []}
                                        onChange={(e) => {
                                            const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                                            setFilters({ ...filters, [column.id]: value });
                                        }}
                                    >
                                        {options.map(opt => (
                                            <MenuItem key={opt} value={opt}>
                                                <Checkbox checked={(filters[column.id] || []).indexOf(opt) > -1} />
                                                <ListItemText primary={opt} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        }
                        else {
                            if (column.label) {
                                return (
                                    <TextField
                                        key={column.id}
                                        label={column.label}
                                        value={filters[column.id] || ''}
                                        onChange={(e) =>
                                            setFilters({ ...filters, [column.id]: e.target.value })
                                        }
                                        variant="outlined"
                                        margin="normal"
                                        fullWidth
                                    />
                                );
                            }
                        }
                    })}
                </Stack>
                <Divider sx={{ width: "100%", paddingTop: "10px" }} />
                <Stack
                    mr={2}
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    paddingY={2}
                    paddingX={1}
                >
                    <ClearButton onClick={() => clearFilters()} />
                    <FilterButton onClick={() => getFilteredDHLReports()} />
                </Stack>
            </CustomFilter>

            <Card>
                {loading ? (
                    <Backdrop
                        sx={{ color: "#fff", zIndex: 99, height: "100%" }}
                        open={loading}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                ) : (
                    <>
                        <DynamicTable
                            data={reportList}
                            headers={TABLE_HEAD}
                            isOrdered={true}
                            initialOrder="operationDate"
                            initalOrderDirection="desc"
                            isMobile={isMobile}
                            handleModal={handleModal}
                            handleCloseModal={handleCloseModal}
                        />

                        {/* Detay Modal */}
                        <Dialog open={openDetail} onClose={handleCloseDetail} fullWidth maxWidth="sm">
                            <DialogTitle>Detay Düzenle</DialogTitle>
                            <DialogContent>
                                <Stack spacing={2} mt={1}>
                                    {/* İşlem Tarihi */}
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DatePicker
                                            label="İşlem Tarihi"
                                            format="DD/MM/YYYY"
                                            value={formData.operationDate || null}
                                            onChange={(newValue) => handleFormChange('operationDate', newValue)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                        {/* Teslim Tarihi */}
                                        <DatePicker
                                            label="Teslim Tarihi"
                                            format="DD/MM/YYYY"
                                            value={formData.deliveredDate || null}
                                            onChange={(newValue) => handleFormChange('deliveredDate', newValue)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                    {/* Konşimento Numarası (Sadece sayı - numeric input) */}
                                    <TextField
                                        label="Konşimento Numarası (Waybill)"
                                        type="number"
                                        value={formData.waybill || ''}
                                        onChange={(e) => handleFormChange('waybill', e.target.value)}
                                        fullWidth
                                    />
                                    {/* Gönderen Adı (Uppercase) */}
                                    <TextField
                                        label="Gönderen Adı"
                                        value={formData.senderName || ''}
                                        onChange={(e) => handleFormChange('senderName', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* Alıcı Adı (Uppercase) */}
                                    <TextField
                                        label="Alıcı Adı"
                                        value={formData.receiverName || ''}
                                        onChange={(e) => handleFormChange('receiverName', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* İşlem Yapan EP Çalışanı (Uppercase) */}
                                    <TextField
                                        label="İşlem Yapan EP Çalışanı"
                                        value={formData.processingStaff || ''}
                                        onChange={(e) => handleFormChange('processingStaff', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* Teslim Eden EP Çalışanı (Uppercase) */}
                                    <TextField
                                        label="Teslim Eden EP Çalışanı"
                                        value={formData.deliveredStaff || ''}
                                        onChange={(e) => handleFormChange('deliveredStaff', e.target.value.toLocaleUpperCase("tr-TR"))}
                                        fullWidth
                                    />
                                    {/* Tutar (Sadece sayı - numeric input, ondalık kabul eder) */}
                                    <TextField
                                        label="Tutar (TL)"
                                        type="number"
                                        inputProps={{ step: "0.01" }}
                                        value={formData.price || ''}
                                        onChange={(e) => handleFormChange('price', e.target.value)}
                                        fullWidth
                                    />
                                    {/* Açıklama - Select (Değişiklik yapılmadan) */}
                                    <FormControl fullWidth>
                                        <InputLabel id="description-detail-label">Açıklama</InputLabel>
                                        <Select
                                            labelId="description-detail-label"
                                            value={formData.description || ''}
                                            label="Açıklama"
                                            onChange={(e) => handleFormChange('description', e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>Seçiniz</em>
                                            </MenuItem>
                                            {['Hazır Gönderi', 'Ücretli Gönderi', 'Amazon İadesi', 'DHL Dağıtım'].map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {/* Not (additionalNote - harici, dönüşüm yapılmadan) */}
                                    <TextField
                                        label="Not"
                                        value={formData.additionalNote || ''}
                                        onChange={(e) => handleFormChange('additionalNote', e.target.value)}
                                        fullWidth
                                    />
                                </Stack>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleCloseDetail}>İptal</Button>
                                <Button
                                    onClick={handleSaveDetail}
                                    variant="contained"
                                    disabled={!isDetailFormValid()}  // Form valid değilse buton disabled olacak
                                >
                                    Kaydet
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </>
                )}
            </Card>


        </div>
    );
}
